import { type IncidentDto, IncidentSeverity, type IncidentStatus } from '@api/incidents';

export { IncidentSeverity };

export enum IncidentColumn {
  COMMENTS = 'comments',
  INDEX = 'index',
  TICKET_NUMBER = 'ticketNumber',
  STATUS = 'status',
  INCIDENT_RULE = 'incidentRule',
  DEVICE_ID = 'deviceId',
  MANUFACTURER = 'manufacturer',
  MODEL = 'model',
  SERIAL_NUMBER = 'serialNumber',
  BUILDING = 'building',
  FLOOR = 'floor',
  ROOM = 'room',
  DEVICE_TYPE = 'deviceType',
  REPORTED = 'reported',
  RESOLVED = 'resolved',
}

export const incidentColumnTitle: Record<string, string> = {
  [IncidentColumn.STATUS]: 'Severity',
  [IncidentColumn.REPORTED]: 'Reported',
  [IncidentColumn.RESOLVED]: 'Resolved',
  [IncidentColumn.INCIDENT_RULE]: 'Incident Rule',
  [IncidentColumn.DEVICE_ID]: 'Device Name',
  [IncidentColumn.DEVICE_TYPE]: 'Device Type',
  [IncidentColumn.MANUFACTURER]: 'Manufacturer',
  [IncidentColumn.MODEL]: 'Model',
  [IncidentColumn.SERIAL_NUMBER]: 'Serial №',
  [IncidentColumn.BUILDING]: 'Building Name',
  [IncidentColumn.FLOOR]: 'Floor',
  [IncidentColumn.ROOM]: 'Room',
  [IncidentColumn.COMMENTS]: 'Comments',
};

export const incidentColumnOptions = Object.keys(incidentColumnTitle).map(key => ({
  title: incidentColumnTitle[key],
  value: key,
}));

export enum IncidentTabStatusFilter {
  OPEN = 'open',
  HISTORY = 'history',
}

export interface NormalizedIncident {
  assignedUser: string;
  building: string;
  buildingId: string;
  deviceId: string;
  deviceName: string;
  deviceType: string;
  deviceTypeId: string;
  floor: string;
  floorId: string;
  id: string;
  incidentRule: string;
  manufacturer: string;
  model: string;
  reported: string;
  resolved: string;
  room: string;
  roomId: string;
  serialNumber: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  ticketNumber: string;
}

const getStringValue = (value: unknown) => (typeof value === 'string' ? value : '');

export const normalizeIncidents = (incidents: IncidentDto[]): NormalizedIncident[] =>
  incidents.map(incident => ({
    assignedUser: '',
    building: incident.device.location?.friendlyName ?? '',
    buildingId: incident.device.location?.id ?? '',
    deviceId: incident.device.id,
    deviceName: incident.device.friendlyName,
    deviceType: incident.device.deviceModelInformation?.deviceType || '',
    deviceTypeId: incident.device.deviceModelInformation?.type?.id || '',
    floor: incident.device.spacePath?.[0]?.friendlyName || '',
    floorId: incident.device.spacePath?.[0]?.id || '',
    id: incident.id,
    incidentRule: incident.incidentRule?.name
      || (incident.incidentRule
        ? `${incident.incidentRule.code} ${incident.incidentRule.operator} ${incident.incidentRule.conditionValue}`
        : ''),
    manufacturer: incident.device.deviceModelInformation?.make?.name || '',
    model: incident.device.deviceModelInformation?.name || '',
    parentId: incident.device?.parentDevice?.id || '',
    parentName: incident.device?.parentDevice?.friendlyName || '',
    reported: new Date(incident.createdTimestamp).toLocaleString('en-US'),
    resolved: incident.resolvedTimestamp ? new Date(incident.resolvedTimestamp).toLocaleString('en-US') : '',
    room: incident.device.spacePath?.[1]?.friendlyName || '',
    roomId: incident.device.spacePath?.[1]?.id || '',
    serialNumber: getStringValue(incident.device.additionalAttributes?.find(attr => attr.name === 'serialNumber')?.value),
    severity: incident.severity,
    status: incident.status as IncidentStatus,
    ticketNumber: `${incident.incidentNumber}`,
  }));
